/**
 * Sæson og ferieplan — bestyrelsens vigtigste vedligeholdelse.
 *
 * HVORFOR DEN SIDE FINDES
 * Alt andet i appen hviler på, at kalenderen er rigtig. Én person skal en
 * gang i halvåret kunne sætte «Efterår 2026 løber fra 10. august til 20.
 * december» og «der er lukket i efterårsferien og i julen» — og så passer
 * kalenderen for alle 400 medlemmer resten af sæsonen.
 *
 * Den vigtigste garanti står på skærmen med rene ord: at oprette eller
 * genberegne en sæson rører ALDRIG en aflysning, en flytning eller en ekstra
 * træning. De er menneskelige beslutninger, og en maskine må ikke overskrive
 * dem.
 */

import { useState } from 'react';
import { api } from '../lib/api.js';
import {
  ActionButton,
  AsyncSection,
  Badge,
  EmptyState,
  Field,
  Notice,
  useAction,
  useAsync,
  PageHead,
} from '../components/ui.js';

interface Season {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  active: boolean;
}

interface Closure {
  id: string;
  seasonId: string;
  name: string;
  startDate: string;
  endDate: string;
}

interface SeasonData {
  seasons: Season[];
  active: Season | null;
  closures: Closure[];
  summary: { scheduled: number; cancelled: number; moved: number; closed: number; extra: number } | null;
}

export function SeasonsPage({ isBoard }: { isBoard: boolean }): JSX.Element {
  const state = useAsync<SeasonData>(() => api.get('/api/seasons'));

  return (
    <div className="stack">
      <PageHead title="Sæson og ferieplan" eyebrow="Administration" eyebrowIcon="season" />
      <AsyncSection state={state} skeletonLines={5}>
        {(data) => (
          <>
            {!data.active ? (
              <Notice kind="warning">
                Der er ingen aktiv sæson. Kalenderen er tom for alle medlemmer, indtil der oprettes
                én.
              </Notice>
            ) : (
              <section className="card">
                <div className="card__header">
                  <h2>{data.active.name}</h2>
                  <Badge kind="ok">Aktiv</Badge>
                </div>
                <p className="small muted mt-1">
                  {data.active.startDate} til {data.active.endDate}
                </p>
                {data.summary ? (
                  <div className="row" style={{ gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                    <Badge kind="ok">{data.summary.scheduled} planlagte</Badge>
                    <Badge kind="warning">{data.summary.cancelled} aflyste</Badge>
                    <Badge kind="warning">{data.summary.moved} flyttede</Badge>
                    <Badge kind="muted">{data.summary.closed} ferielukkede</Badge>
                    <Badge>{data.summary.extra} ekstra</Badge>
                  </div>
                ) : null}
                {isBoard ? <RegenerateButton seasonId={data.active.id} onDone={state.reload} /> : null}
              </section>
            )}

            {data.active ? (
              <ClosuresSection
                season={data.active}
                closures={data.closures}
                isBoard={isBoard}
                onChanged={state.reload}
              />
            ) : null}

            {isBoard ? <NewSeasonForm onCreated={state.reload} /> : null}

            {data.seasons.length > 1 ? (
              <section className="card">
                <h2>Tidligere sæsoner</h2>
                <ul className="list mt-2">
                  {data.seasons
                    .filter((s) => !s.active)
                    .map((season) => (
                      <li key={season.id} className="row row--between">
                        <div>
                          <div className="small">
                            <strong>{season.name}</strong>
                          </div>
                          <div className="tiny muted">
                            {season.startDate} til {season.endDate}
                          </div>
                        </div>
                        {isBoard ? <ActivateButton season={season} onDone={state.reload} /> : null}
                      </li>
                    ))}
                </ul>
                <p className="tiny muted mt-3">
                  Fremmøde og afbud fra tidligere sæsoner bevares. At gøre en gammel sæson aktiv
                  igen ændrer kun, hvad medlemmerne ser i kalenderen.
                </p>
              </section>
            ) : null}

            {!isBoard ? (
              <p className="tiny muted">
                Kun bestyrelsen kan oprette sæsoner og ferielukninger. Du kan aflyse og flytte
                enkelte træninger på dine egne hold.
              </p>
            ) : null}
          </>
        )}
      </AsyncSection>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Genberegning                                                        */
/* ------------------------------------------------------------------ */

function RegenerateButton({
  seasonId,
  onDone,
}: {
  seasonId: string;
  onDone: () => void;
}): JSX.Element {
  const action = useAction();

  const run = (): void => {
    void action.run(async () => {
      const result = await api.post<{
        created: number;
        updated: number;
        removed: number;
        note: string;
      }>(`/api/seasons/${encodeURIComponent(seasonId)}/regenerate`);
      action.setSuccess(
        `${result.created} nye træninger, ${result.updated} opdaterede, ${result.removed} fjernet. ${result.note}`,
      );
      onDone();
    });
  };

  return (
    <div className="mt-4">
      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}
      <ActionButton className="btn btn--secondary" pending={action.pending} onClick={run}>
        Genberegn træninger
      </ActionButton>
      <p className="tiny muted mt-2">
        Bruges, når et hold har fået ny ugedag eller nyt tidspunkt i Conventus. Aflyste, flyttede og
        ekstra træninger bevares uændret — genberegningen rører dem ikke.
      </p>
    </div>
  );
}

function ActivateButton({ season, onDone }: { season: Season; onDone: () => void }): JSX.Element {
  const action = useAction();
  return (
    <ActionButton
      className="btn btn--secondary btn--small"
      pending={action.pending}
      onClick={() =>
        void action.run(async () => {
          await api.post(`/api/seasons/${encodeURIComponent(season.id)}/activate`);
          onDone();
        })
      }
    >
      Gør aktiv
    </ActionButton>
  );
}

/* ------------------------------------------------------------------ */
/* Ferielukning                                                        */
/* ------------------------------------------------------------------ */

function ClosuresSection({
  season,
  closures,
  isBoard,
  onChanged,
}: {
  season: Season;
  closures: Closure[];
  isBoard: boolean;
  onChanged: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const action = useAction();

  const create = (): void => {
    void action.run(async () => {
      const result = await api.post<{ closedSessions: number; note: string }>(
        `/api/seasons/${encodeURIComponent(season.id)}/closures`,
        { name, startDate, endDate },
      );
      action.setSuccess(
        `${result.closedSessions} træninger er markeret som ferielukkede. ${result.note}`,
      );
      setOpen(false);
      setName('');
      setStartDate('');
      setEndDate('');
      onChanged();
    });
  };

  const remove = (closure: Closure): void => {
    void action.run(async () => {
      const result = await api.del<{ reopened: number }>(
        `/api/closures/${encodeURIComponent(closure.id)}`,
      );
      action.setSuccess(`«${closure.name}» er fjernet. ${result.reopened} træninger er åbnet igen.`);
      onChanged();
    });
  };

  return (
    <section className="card">
      <div className="card__header">
        <h2>Ferier og lukkedage</h2>
        {isBoard && !open ? (
          <button type="button" className="btn btn--secondary btn--small" onClick={() => setOpen(true)}>
            Tilføj
          </button>
        ) : null}
      </div>
      <p className="tiny muted mt-1">
        Alle hold holder fri i de perioder, der står her. Træningerne bliver stående i kalenderen,
        men markeret som ferielukket — så ingen møder forgæves op.
      </p>

      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      {open ? (
        <div className="stack stack--tight mt-3">
          <Field label="Navn" htmlFor="closure-name" hint="Fx «Efterårsferie» eller «Juleferie».">
            <input
              id="closure-name"
              type="text"
              maxLength={60}
              value={name}
              onChange={(e) => setName((e.target as HTMLInputElement).value)}
            />
          </Field>
          <div className="row" style={{ gap: 10 }}>
            <Field label="Første lukkedag" htmlFor="closure-start">
              <input
                id="closure-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate((e.target as HTMLInputElement).value)}
              />
            </Field>
            <Field label="Sidste lukkedag" htmlFor="closure-end">
              <input
                id="closure-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate((e.target as HTMLInputElement).value)}
              />
            </Field>
          </div>
          <div className="btn-group">
            <ActionButton
              pending={action.pending}
              onClick={create}
              disabled={!name.trim() || !startDate || !endDate}
            >
              Luk perioden
            </ActionButton>
            <button type="button" className="btn btn--secondary" onClick={() => setOpen(false)}>
              Fortryd
            </button>
          </div>
        </div>
      ) : null}

      {closures.length === 0 ? (
        <EmptyState title="Ingen ferier oprettet">
          Der er ikke lagt lukkedage ind for denne sæson endnu.
        </EmptyState>
      ) : (
        <ul className="list mt-3">
          {closures.map((closure) => (
            <li key={closure.id} className="row row--between">
              <div>
                <div className="small">
                  <strong>{closure.name}</strong>
                </div>
                <div className="tiny muted">
                  {closure.startDate} til {closure.endDate}
                </div>
              </div>
              {isBoard ? (
                <ActionButton
                  className="btn btn--ghost btn--small"
                  pending={action.pending}
                  onClick={() => remove(closure)}
                >
                  Fjern
                </ActionButton>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Ny sæson                                                            */
/* ------------------------------------------------------------------ */

function NewSeasonForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const action = useAction();

  const create = (): void => {
    void action.run(async () => {
      const result = await api.post<{
        createdSessions: number;
        skipped: Array<{ teamId: string; reason: string }>;
        note: string | null;
      }>('/api/seasons', { name, startDate, endDate, activate: true });
      action.setSuccess(
        `Sæsonen er oprettet med ${result.createdSessions} træninger.${
          result.skipped.length ? ` ${result.note}` : ''
        }`,
      );
      setOpen(false);
      setName('');
      onCreated();
    });
  };

  return (
    <section className="card">
      <div className="card__header">
        <h2>Ny sæson</h2>
        {!open ? (
          <button type="button" className="btn btn--secondary btn--small" onClick={() => setOpen(true)}>
            Opret
          </button>
        ) : null}
      </div>
      <p className="tiny muted mt-1">
        Alle holds træninger lægges automatisk ud over perioden ud fra holdets ugedag og tidspunkt.
        Bagefter tilføjes ferierne.
      </p>

      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      {open ? (
        <div className="stack stack--tight mt-3">
          <Field label="Navn" htmlFor="season-name" hint="Fx «Efterår 2026» eller «Forår 2027».">
            <input
              id="season-name"
              type="text"
              maxLength={60}
              value={name}
              onChange={(e) => setName((e.target as HTMLInputElement).value)}
            />
          </Field>
          <div className="row" style={{ gap: 10 }}>
            <Field label="Første træningsdag" htmlFor="season-start">
              <input
                id="season-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate((e.target as HTMLInputElement).value)}
              />
            </Field>
            <Field label="Sidste træningsdag" htmlFor="season-end">
              <input
                id="season-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate((e.target as HTMLInputElement).value)}
              />
            </Field>
          </div>
          <Notice kind="warning">
            Den nye sæson bliver med det samme den, medlemmerne ser i kalenderen. Den nuværende
            sæson slettes ikke — den bliver bare ikke længere den aktive.
          </Notice>
          <div className="btn-group">
            <ActionButton
              pending={action.pending}
              onClick={create}
              disabled={!name.trim() || !startDate || !endDate}
            >
              Opret sæson
            </ActionButton>
            <button type="button" className="btn btn--secondary" onClick={() => setOpen(false)}>
              Fortryd
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
